import { useState } from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollText, Shield, Lock, Scale, FileText, AlertTriangle } from 'lucide-react';

export default function Legal() {
  const [activeTab, setActiveTab] = useState('terms');

  return (
    <main data-testid="legal-page" className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-card to-background py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
              <Scale className="w-8 h-8 text-gold" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">Legal Information</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Important terms, policies, and guidelines for using AutoTrust Ghana.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full flex flex-wrap mb-8 gap-2 bg-transparent">
              <TabsTrigger value="terms" className="flex-1 min-w-[140px] data-[state=active]:bg-gold data-[state=active]:text-black">
                <FileText className="w-4 h-4 mr-2" /> Terms of Service
              </TabsTrigger>
              <TabsTrigger value="privacy" className="flex-1 min-w-[140px] data-[state=active]:bg-gold data-[state=active]:text-black">
                <Lock className="w-4 h-4 mr-2" /> Privacy Policy
              </TabsTrigger>
              <TabsTrigger value="escrow" className="flex-1 min-w-[140px] data-[state=active]:bg-gold data-[state=active]:text-black">
                <Shield className="w-4 h-4 mr-2" /> Escrow Terms
              </TabsTrigger>
            </TabsList>

            {/* Terms of Service */}
            <TabsContent value="terms" className="prose prose-invert max-w-none">
              <div className="bg-card border border-border/50 rounded-xl p-8 md:p-10 space-y-8">
                <div>
                  <p className="text-sm text-muted-foreground mb-4">Last Updated: February 2026</p>
                  <h2 className="text-2xl font-bold mb-4">Terms of Service</h2>
                  <p className="text-muted-foreground">
                    Welcome to AutoTrust Ghana. By accessing or using our platform, you agree to be bound by these Terms of Service. Please read them carefully before using our services.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">1. Definitions</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li><strong>"Platform"</strong> refers to the AutoTrust Ghana website and mobile applications.</li>
                    <li><strong>"User"</strong> means any individual or entity accessing or using our services.</li>
                    <li><strong>"Seller"</strong> refers to users who list vehicles for sale on the Platform.</li>
                    <li><strong>"Buyer"</strong> refers to users who purchase vehicles through the Platform.</li>
                    <li><strong>"Listing"</strong> means any vehicle advertisement posted on the Platform.</li>
                    <li><strong>"Escrow"</strong> refers to our secure payment holding service.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">2. Eligibility</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>You must be at least 18 years old to use our services.</li>
                    <li>You must be legally capable of entering into binding contracts under Ghanaian law.</li>
                    <li>You must provide accurate and complete registration information.</li>
                    <li>Businesses must be properly registered in Ghana to use dealer features.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">3. User Accounts</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                    <li>You must notify us immediately of any unauthorized use of your account.</li>
                    <li>One person may maintain only one account unless approved as a dealer.</li>
                    <li>We reserve the right to suspend or terminate accounts that violate these terms.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">4. Listing Requirements</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>All listings must be for vehicles legally owned by the seller or which the seller is authorized to sell.</li>
                    <li>Listings must include accurate VIN (Vehicle Identification Number) information.</li>
                    <li>Photos must be original and accurately represent the vehicle's current condition.</li>
                    <li>Prices must be stated in Ghana Cedis (GHS) and represent the actual asking price.</li>
                    <li>Sellers must disclose all known defects, accidents, or material issues.</li>
                    <li>Contact information in photos or descriptions is prohibited.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">5. Prohibited Activities</h3>
                  <p className="text-muted-foreground mb-2">Users are prohibited from:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Listing stolen vehicles or vehicles with undisclosed liens.</li>
                    <li>Providing false or misleading information in listings.</li>
                    <li>Manipulating images to hide defects or misrepresent vehicle condition.</li>
                    <li>Circumventing the escrow system for transactions initiated on the Platform.</li>
                    <li>Harassing, threatening, or defrauding other users.</li>
                    <li>Using the Platform for money laundering or illegal activities.</li>
                    <li>Creating multiple accounts to evade bans or restrictions.</li>
                    <li>Scraping or harvesting user data from the Platform.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">6. Trust Score & Verification</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Trust Scores are calculated automatically based on VIN verification, image analysis, and price validation.</li>
                    <li>Trust Scores are informational and do not guarantee the legitimacy of any listing.</li>
                    <li>KYC verification is optional but recommended for sellers to build buyer confidence.</li>
                    <li>We reserve the right to modify Trust Score algorithms without notice.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">7. Fees & Payments</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Listing vehicles on the Platform is free of charge.</li>
                    <li>Escrow transactions are subject to a service fee disclosed at checkout.</li>
                    <li>Payment processing fees are borne by the buyer unless otherwise specified.</li>
                    <li>All fees are non-refundable except where required by law or our Escrow Terms.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">8. Limitation of Liability</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>AutoTrust Ghana is a marketplace facilitator, not a party to transactions between users.</li>
                    <li>We do not guarantee the condition, legality, or quality of listed vehicles.</li>
                    <li>We are not liable for disputes between users conducted outside our escrow system.</li>
                    <li>Our maximum liability is limited to fees paid to us in the preceding 12 months.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">9. Dispute Resolution</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Disputes arising from escrow transactions will be handled per our Escrow Terms.</li>
                    <li>For other disputes, parties should first attempt amicable resolution.</li>
                    <li>Unresolved disputes shall be subject to arbitration in Accra, Ghana.</li>
                    <li>Ghanaian law governs these terms and any disputes arising from them.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">10. Changes to Terms</h3>
                  <p className="text-muted-foreground">
                    We may modify these terms at any time. Continued use of the Platform after changes constitutes acceptance. Material changes will be communicated via email or Platform notification.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">11. Contact</h3>
                  <p className="text-muted-foreground">
                    For questions about these terms, contact us at:<br />
                    Email: legal@autotrustgh.com<br />
                    Address: Accra, Ghana
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Privacy Policy */}
            <TabsContent value="privacy" className="prose prose-invert max-w-none">
              <div className="bg-card border border-border/50 rounded-xl p-8 md:p-10 space-y-8">
                <div>
                  <p className="text-sm text-muted-foreground mb-4">Last Updated: February 2026</p>
                  <h2 className="text-2xl font-bold mb-4">Privacy Policy</h2>
                  <p className="text-muted-foreground">
                    AutoTrust Ghana ("we", "us", "our") is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your personal information in compliance with Ghana's Data Protection Act, 2012 (Act 843).
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">1. Information We Collect</h3>
                  <p className="text-muted-foreground mb-2"><strong>Account Information:</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-4">
                    <li>Name, email address, phone number</li>
                    <li>Location (city)</li>
                    <li>Profile picture (optional)</li>
                    <li>KYC documents (for verified sellers)</li>
                  </ul>
                  <p className="text-muted-foreground mb-2"><strong>Listing Information:</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-4">
                    <li>Vehicle details (VIN, make, model, year, price)</li>
                    <li>Vehicle photos</li>
                    <li>Listing descriptions</li>
                  </ul>
                  <p className="text-muted-foreground mb-2"><strong>Usage Information:</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>IP address and device information</li>
                    <li>Browser type and settings</li>
                    <li>Pages visited and actions taken</li>
                    <li>Search queries</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">2. How We Use Your Information</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>To provide and improve our marketplace services</li>
                    <li>To verify user identity and prevent fraud</li>
                    <li>To process escrow transactions</li>
                    <li>To facilitate communication between buyers and sellers</li>
                    <li>To send service-related notifications</li>
                    <li>To analyze usage patterns and improve user experience</li>
                    <li>To comply with legal obligations</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">3. Information Sharing</h3>
                  <p className="text-muted-foreground mb-2">We may share your information with:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li><strong>Other Users:</strong> Your name and listing information are visible to other users. Contact details are never shared without consent.</li>
                    <li><strong>Payment Processors:</strong> Flutterwave processes payments and receives necessary transaction information.</li>
                    <li><strong>Service Providers:</strong> Third parties who help us operate the Platform (hosting, analytics, AI services).</li>
                    <li><strong>Law Enforcement:</strong> When required by law or to protect rights and safety.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">4. Data Security</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>We use industry-standard encryption (TLS/SSL) for data transmission.</li>
                    <li>Passwords are hashed using bcrypt algorithm.</li>
                    <li>KYC documents are stored securely and access is restricted.</li>
                    <li>We regularly audit our security practices.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">5. Your Rights</h3>
                  <p className="text-muted-foreground mb-2">Under Ghana's Data Protection Act, you have the right to:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Access your personal data</li>
                    <li>Correct inaccurate data</li>
                    <li>Request deletion of your data (subject to legal requirements)</li>
                    <li>Object to processing of your data</li>
                    <li>Data portability</li>
                  </ul>
                  <p className="text-muted-foreground mt-4">
                    To exercise these rights, contact us at privacy@autotrustgh.com
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">6. Data Retention</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Account data is retained while your account is active.</li>
                    <li>Listing data is retained for 2 years after the listing is closed.</li>
                    <li>Transaction records are retained for 7 years for legal compliance.</li>
                    <li>You may request account deletion at any time.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">7. Cookies & Tracking</h3>
                  <p className="text-muted-foreground">
                    We use cookies and similar technologies to improve user experience, analyze traffic, and personalize content. You can control cookie settings through your browser.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">8. Children's Privacy</h3>
                  <p className="text-muted-foreground">
                    Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">9. Changes to This Policy</h3>
                  <p className="text-muted-foreground">
                    We may update this policy periodically. Changes will be posted on this page with an updated revision date. Material changes will be communicated via email.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">10. Contact Us</h3>
                  <p className="text-muted-foreground">
                    Data Controller: AutoTrust Ghana<br />
                    Email: privacy@autotrustgh.com<br />
                    Address: Accra, Ghana
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Escrow Terms */}
            <TabsContent value="escrow" className="prose prose-invert max-w-none">
              <div className="bg-card border border-border/50 rounded-xl p-8 md:p-10 space-y-8">
                <div>
                  <p className="text-sm text-muted-foreground mb-4">Last Updated: February 2026</p>
                  <h2 className="text-2xl font-bold mb-4">Escrow Service Terms</h2>
                  <p className="text-muted-foreground">
                    These terms govern the use of AutoTrust Ghana's secure escrow payment service, powered by Flutterwave. By using our escrow service, you agree to these terms.
                  </p>
                </div>

                <div className="bg-trust-green/10 border border-trust-green/30 rounded-lg p-4">
                  <h4 className="font-semibold text-trust-green flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5" /> How Escrow Protects You
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Escrow holds the buyer's payment securely until the vehicle is delivered and inspected. This protects buyers from paying for vehicles they never receive, and protects sellers by confirming buyer's funds are available before delivery.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">1. Escrow Process</h3>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                    <li><strong>Initiation:</strong> Buyer clicks "Buy with Secure Escrow" and completes payment.</li>
                    <li><strong>Holding:</strong> Funds are held securely by Flutterwave (our payment partner).</li>
                    <li><strong>Delivery:</strong> Seller delivers the vehicle to the buyer.</li>
                    <li><strong>Inspection:</strong> Buyer inspects the vehicle (recommended within 48 hours).</li>
                    <li><strong>Confirmation:</strong> Buyer confirms delivery in the app.</li>
                    <li><strong>Release:</strong> Funds are released to seller within 24-48 hours.</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">2. Fees</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Escrow service fee: 1.5% of transaction value (minimum GHS 50)</li>
                    <li>Payment processing fee: As charged by payment provider</li>
                    <li>Fees are non-refundable except where a full refund is granted</li>
                    <li>Seller receives the full listing price minus applicable fees</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">3. Buyer Obligations</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Complete payment within the specified timeframe</li>
                    <li>Inspect the vehicle promptly upon delivery</li>
                    <li>Confirm delivery within 7 days of receipt, or raise a dispute</li>
                    <li>Provide accurate delivery address and contact information</li>
                    <li>Not make false or fraudulent disputes</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">4. Seller Obligations</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Deliver the vehicle as described in the listing</li>
                    <li>Provide all necessary documentation (ownership proof, roadworthy certificate)</li>
                    <li>Arrange delivery within 7 days of payment confirmation</li>
                    <li>Respond to buyer inquiries promptly</li>
                    <li>Not misrepresent vehicle condition or history</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">5. Disputes</h3>
                  <p className="text-muted-foreground mb-4">
                    If there's an issue with the transaction, either party can open a dispute before delivery is confirmed.
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li><strong>Eligible Dispute Reasons:</strong> Vehicle not as described, non-delivery, documentation issues, mechanical problems not disclosed.</li>
                    <li><strong>AI Analysis:</strong> Our AI system analyzes disputes and provides resolution recommendations.</li>
                    <li><strong>Resolution Options:</strong> Full refund to buyer, partial refund, or completing the transaction.</li>
                    <li><strong>Timeline:</strong> Disputes are typically resolved within 5-7 business days.</li>
                    <li><strong>Escalation:</strong> Complex cases are reviewed by our human support team.</li>
                  </ul>
                </div>

                <div className="bg-alert-red/10 border border-alert-red/30 rounded-lg p-4">
                  <h4 className="font-semibold text-alert-red flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5" /> Important Warning
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Never confirm delivery until you have physically received and inspected the vehicle. Once delivery is confirmed, funds are released and cannot be recalled except through the dispute process.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">6. Refund Policy</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li><strong>Full Refund:</strong> If seller fails to deliver or vehicle is significantly not as described.</li>
                    <li><strong>Partial Refund:</strong> If vehicle has minor issues not disclosed, amount determined by dispute resolution.</li>
                    <li><strong>No Refund:</strong> If buyer confirms delivery, or dispute is decided in seller's favor.</li>
                    <li><strong>Refund Timeline:</strong> 5-10 business days to original payment method.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">7. Cancellation</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Buyer may cancel before seller confirms shipment (full refund minus processing fees).</li>
                    <li>Seller may cancel if unable to deliver (full refund to buyer).</li>
                    <li>Cancellation after shipment requires dispute process.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">8. Limitation of Liability</h3>
                  <p className="text-muted-foreground">
                    AutoTrust Ghana facilitates escrow transactions but is not a party to the sale. Our liability is limited to the proper handling of escrow funds. We do not guarantee vehicle condition, title, or fitness for purpose.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">9. Contact for Escrow Issues</h3>
                  <p className="text-muted-foreground">
                    For urgent escrow matters, contact our support team:<br />
                    Email: escrow@autotrustgh.com<br />
                    Phone: Available in your Dashboard under "Support"
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
}
